import React, { ComponentType, useCallback, useState } from "react";

import cn from "classnames";
import { isFunction } from "lodash-es";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Modal, ModalProps } from "@mapmycustomers/ui";

import IWizardStep from "@app/component/wizard/types/IWizardStep";

import styles from "./BaseWizard.module.scss";

export interface BaseWizardProps<T> extends Pick<
  ModalProps,
  "className" | "closable" | "maskClosable" | "width" | "zIndex"
> {
  initialState: T;
  initialStep?: number;
  onFinish?: (finalState: T) => void;
  onGetStep: (index: number) => ComponentType<IWizardStep<T>>;
  onHide: (finished: boolean) => void;
  onStepChange?: (prevIndex: number, nextIndex: number) => void;
  stepsCount: number;
}

const BaseWizard = <T,>({
  className,
  initialState,
  initialStep,
  onFinish,
  onGetStep,
  onHide,
  onStepChange,
  stepsCount,
  ...modalProps
}: BaseWizardProps<T>) => {
  const [state, setState] = useState(initialState);
  const [currentStepIndex, setCurrentStepIndex] = useState(
    isDefined(initialStep) && initialStep >= 0 && initialStep < stepsCount ? initialStep : 0
  );

  const handleCancel = useCallback(() => {
    onHide(false);
  }, [onHide]);

  const handleFinish = useCallback(() => {
    setState((state) => {
      // calling onFinish within setState to guarantee the latest state value
      // calling onHide here too because otherwise, React may drop processing remaining
      // setState calls and onFinish will not be called.
      onFinish?.(state);
      onHide(true);
      return state;
    });
  }, [onHide, onFinish]);

  const handleGoBack = useCallback(() => {
    setCurrentStepIndex((index) => {
      const resultingIndex = index > 0 ? index - 1 : index;
      if (resultingIndex !== index) {
        onStepChange?.(index, resultingIndex);
      }
      return resultingIndex;
    });
  }, [onStepChange]);

  const handleGoForward = useCallback(() => {
    setCurrentStepIndex((index) => {
      const resultingIndex = index < stepsCount - 1 ? index + 1 : index;
      if (resultingIndex !== index) {
        onStepChange?.(index, resultingIndex);
      }
      return resultingIndex;
    });
  }, [stepsCount, onStepChange]);

  const handleGoToStep = useCallback(
    (newIndex: number) => {
      setCurrentStepIndex((index) => {
        const resultingIndex = newIndex >= 0 && newIndex < stepsCount ? newIndex : index;
        if (resultingIndex !== index) {
          onStepChange?.(index, resultingIndex);
        }
        return resultingIndex;
      });
    },
    [stepsCount, onStepChange]
  );

  const handleSetState = useCallback((state: ((prevState: T) => T) | Partial<T>) => {
    if (isFunction(state)) {
      setState(state);
    } else {
      setState((prevState) => ({ ...prevState, ...state }));
    }
  }, []);

  if (stepsCount < 1) {
    return null;
  }

  const CurrentStepComponent = onGetStep(currentStepIndex);

  return (
    <Modal
      {...modalProps}
      className={cn(styles.container, className)}
      closable={false}
      footer={null}
      onCancel={handleCancel}
      open
      title={null}
    >
      <CurrentStepComponent
        onCancel={handleCancel}
        onFinish={handleFinish}
        onGoBack={handleGoBack}
        onGoForward={handleGoForward}
        onGoToStep={handleGoToStep}
        onSetState={handleSetState}
        state={state}
        stepIndex={currentStepIndex}
      />
    </Modal>
  );
};

export default BaseWizard;
